import {
  GenericField,
  GenericFieldProps,
} from "@/lib/Components/Form/Fields/GenericField";
import {
  Combobox,
  ComboboxProps,
} from "@/lib/Components/Form/Components/Combobox";
import { cn } from "@/lib/utils";
import { useField } from "formik";
import { ReactNode, useState } from "react";
import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { useGqlQuery, Variables } from "@/lib/GraphQLCodegen/fetcher";
import { keepPreviousData } from "@tanstack/react-query";
import { useFormEditState } from "@/lib/Components/Form/Hooks/useFormEditState";

export type BaseComboboxInputProps<
  TData,
  TVariables extends Variables | undefined,
  TValue,
  RData = any,
  RVariables extends Variables | undefined = any,
> = Partial<ComboboxInputProps<TData, TVariables, TValue, RData, RVariables>> &
  Required<
    Pick<
      ComboboxInputProps<TData, TVariables, TValue, RData, RVariables>,
      "label" | "name"
    >
  >;

export type ComboboxInputProps<
  TData,
  TVariables extends Variables | undefined,
  TValue,
  RData,
  RVariables extends Variables | undefined,
> = {
  document: TypedDocumentNode<TData, TVariables>;
  accessor: (res: TData) => TValue[];
  getQueryVariables: (search: string) => TVariables;
  recordDocument: TypedDocumentNode<RData, RVariables>;
  recordAccessor: (res: RData) => NoInfer<TValue>;
  onChange?: (value: TValue | null) => void;
  getViewNode: (value: TValue | null) => ReactNode;
  requireSearch?: boolean;
} & Omit<GenericFieldProps, "viewNode" | "children"> &
  Pick<
    ComboboxProps<TValue>,
    | "getInputNode"
    | "getListItemNode"
    | "getKey"
    | "fixture"
    | "clearable"
    | "disabled"
    | "LeadingIcon"
    | "autoFocus"
  >;

export function ComboboxInput<
  TData,
  TVariables extends Variables | undefined,
  TValue,
  RData,
  RVariables extends Variables | undefined,
>({
  getInputNode,
  getListItemNode,
  getViewNode,
  getKey,
  document,
  getQueryVariables,

  accessor,
  recordDocument,

  recordAccessor,
  fixture,

  onChange,
  clearable,
  LeadingIcon,
  requireSearch,
  autoFocus,
  ...props
}: ComboboxInputProps<TData, TVariables, TValue, RData, RVariables>) {
  const [field, meta, { setValue }] = useField(props.name);
  const { isEditing } = useFormEditState();
  const showError = !!meta.error && meta.touched;

  const [search, setSearch] = useState("");

  // The option list is only ever shown while editing — view mode renders
  // getViewNode() off the selected record query below.
  const { data, isFetching } = useGqlQuery(
    document,
    getQueryVariables(search),
    {
      placeholderData: keepPreviousData,
      enabled: isEditing && (requireSearch ? search.length > 0 : true),
    },
  );

  const items = data ? accessor(data) : undefined;

  // Fetch the record for the currently selected value so it can be displayed
  // even when it isn't in the current search results — e.g. a value set
  // programmatically or a record just created via a dialog.
  const { data: selectedData } = useGqlQuery(
    recordDocument,
    {
      id: field.value,
    } as unknown as RVariables,
    {
      enabled: !!field.value,
    },
  );

  const selectedRecord = selectedData
    ? recordAccessor(selectedData)
    : undefined;

  // Try to find selected item in current items or the fetched record
  const selected =
    [...(items ?? []), ...(selectedRecord ? [selectedRecord] : [])].find(
      (i) => getKey(i) === field.value,
    ) ?? null;

  return (
    <GenericField viewNode={getViewNode(selected)} {...props}>
      {() => (
        <Combobox
          fixture={fixture}
          buttonProps={{
            className: cn(
              showError
                ? "border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-hidden focus:ring-red-500"
                : "focus:border-indigo-500 focus:ring-indigo-500",
              "border-gray-300 bg-white shadow-xs text-sm transition-shadow duration-100 ring-offset-0",
            ),
          }}
          search={search}
          onSearchChange={setSearch}
          items={items}
          selected={selected}
          isLoading={isFetching}
          onChange={async (newItem) => {
            await setValue(getKey(newItem));
            onChange?.(newItem);
          }}
          onClear={async () => {
            await setValue(null);
            onChange?.(null);
          }}
          getKey={getKey}
          getListItemNode={(item) => getListItemNode(item, search)}
          getInputNode={getInputNode}
          clearable={clearable}
          LeadingIcon={LeadingIcon}
          autoFocus={autoFocus}
        />
      )}
    </GenericField>
  );
}
